import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { normalizeLaunchModuleKey } from "@/lib/launchPath";
import type { AnyValue } from "@/lib/rowTypes";

export interface LaunchPathLesson {
  lesson_id: string;
  lesson_name: string;
  beta_order: number | null;
  course_name: string;
  completed: boolean;
}

export interface LaunchModulePath {
  id: string;
  title: string;
  intro: string;
  moduleKey: string;
  moduleRoute: string;
  moduleName: string;
  lessons: LaunchPathLesson[];
  completedLessons: number;
  totalLessons: number;
  checkpointScore: number;
  requiredScore: number;
  unlocked: boolean;
}

export const LAUNCH_PATHS: Array<{
  id: string;
  title: string;
  intro: string;
  moduleKey: string;
  moduleRoute: string;
  moduleName: string;
}> = [
  {
    id: "money_foundations",
    title: "Money Foundations",
    intro: "Needs vs wants, income, cash flow, and the language of money.",
    moduleKey: "budget_builder",
    moduleRoute: "/budget-builder",
    moduleName: "Budget Builder",
  },
  {
    id: "credit_foundations",
    title: "Credit Foundations",
    intro: "How credit works, what scores mean, and the cost of bad debt.",
    moduleKey: "credit_management",
    moduleRoute: "/credit-management",
    moduleName: "Credit Management",
  },
  {
    id: "scam_defense",
    title: "Scam Defense",
    intro: "Spot urgency, fake offers, and protect your money before you pay.",
    moduleKey: "scam_defense",
    moduleRoute: "/scam-defense",
    moduleName: "Scam Defense",
  },
];

/** Centralized allowlist of module keys that beta users are allowed to play.
 *  Derived from LAUNCH_PATHS so there is exactly one source of truth. */
export const APPROVED_LAUNCH_MODULE_KEYS: ReadonlyArray<string> = LAUNCH_PATHS.map(
  (p) => p.moduleKey,
);

/** Centralized allowlist of route paths that beta users are allowed to open. */
export const APPROVED_LAUNCH_MODULE_ROUTES: ReadonlyArray<string> = LAUNCH_PATHS.map(
  (p) => p.moduleRoute,
);

export function isApprovedLaunchModuleKey(moduleKey: string | null | undefined): boolean {
  return !!moduleKey && APPROVED_LAUNCH_MODULE_KEYS.includes(moduleKey);
}

/** The advanced modules beta users see as "reserved for full launch". */
export const ADVANCED_MODULE_REGEX = /^\/(entrepreneurship|simulator|re-investing|homebuying|vehicle-ownership|development|wealth-strategy|tax-strategy|recovery-mode|risk-protection|family-budget|career-path|finances|econ)/;

type LaunchPathRow = {
  id: string;
  position: number;
  item_type: "lesson" | "simulator";
  ref_id: string;
  title: string;
};

export function useLaunchModules() {
  const { user } = useAuth();
  const [paths, setPaths] = useState<LaunchModulePath[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setPaths([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const client = supabase;
      const { data: rowsData, error: rowsError } = await client
        .from("launch_path_items")
        .select("id, position, item_type, ref_id, title")
        .eq("is_active", true)
        .order("position", { ascending: true });
      if (rowsError) throw rowsError;

      const rows = (rowsData || []) as LaunchPathRow[];
      const lessonRefs = rows.filter((r) => r.item_type === "lesson").map((r) => r.ref_id);
      const simulatorRefs = rows
        .filter((r) => r.item_type === "simulator")
        .map((r) => normalizeLaunchModuleKey(r.ref_id))
        .filter(Boolean);

      const [lessonRowsRes, lessonProgressRes, moduleProgressRes, unlockResults] = await Promise.all([
        lessonRefs.length
          ? client
              .from("lessons")
              .select("lesson_id, lesson_name, course_name")
              .in("lesson_id", lessonRefs)
          : Promise.resolve({ data: [] }),
        lessonRefs.length
          ? client
              .from("user_progress")
              .select("lesson_id")
              .eq("user_id", user.id)
              .eq("completed", true)
              .in("lesson_id", lessonRefs)
          : Promise.resolve({ data: [] }),
        simulatorRefs.length
          ? client
              .from("module_completions")
              .select("module_key")
              .eq("user_id", user.id)
              .eq("passed", true)
              .in("module_key", simulatorRefs)
          : Promise.resolve({ data: [] }),
        Promise.all(
          LAUNCH_PATHS.map(async (path) => [path.id, await checkLaunchModuleUnlocked(path.moduleKey)] as const),
        ),
      ]);

      const lessonById = new Map<string, { lesson_id: string; lesson_name: string; course_name: string }>();
      (lessonRowsRes.data || []).forEach((lesson: AnyValue) => lessonById.set(lesson.lesson_id, lesson));
      const completedLessons = new Set<string>((lessonProgressRes.data || []).map((p: AnyValue) => p.lesson_id));
      const completedModules = new Set<string>((moduleProgressRes.data || []).map((p: AnyValue) => p.module_key));
      const unlockedByPath = new Map<string, boolean>(unlockResults);

      const next: LaunchModulePath[] = LAUNCH_PATHS.map((path, index) => {
        const moduleRow = rows.find(
          (row) => row.item_type === "simulator" && normalizeLaunchModuleKey(row.ref_id) === path.moduleKey,
        );
        const previousModulePosition = LAUNCH_PATHS.slice(0, index)
          .map((previousPath) => rows.find(
            (row) => row.item_type === "simulator" && normalizeLaunchModuleKey(row.ref_id) === previousPath.moduleKey,
          )?.position ?? 0)
          .reduce((max, position) => Math.max(max, position), 0);

        const lessons = rows
          .filter((row) => row.item_type === "lesson")
          .filter((row) => row.position > previousModulePosition && (!moduleRow || row.position < moduleRow.position))
          .map((row) => {
            const lesson = lessonById.get(row.ref_id);
            return {
              lesson_id: row.ref_id,
              lesson_name: lesson?.lesson_name ?? row.title,
              beta_order: row.position,
              course_name: lesson?.course_name ?? "Beta Launch Path",
              completed: completedLessons.has(row.ref_id),
            };
          });
        const completedLessonsCount = lessons.filter((lesson) => lesson.completed).length;
        const checkpointScore = lessons.length > 0
          ? Math.round((completedLessonsCount / lessons.length) * 100)
          : 0;
        const moduleCompleted = completedModules.has(path.moduleKey);

        return {
          ...path,
          lessons,
          completedLessons: completedLessonsCount,
          totalLessons: lessons.length,
          checkpointScore,
          requiredScore: 80,
          unlocked: Boolean(unlockedByPath.get(path.id)) || moduleCompleted,
        };
      });
      setPaths(next);
    } catch (err) {
      console.warn("[useLaunchModules] load failed", err);
      setPaths([]);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [user?.id]);

  useEffect(() => { void load(); }, [load]);

  const evaluate = useCallback(async (pathId: string) => {
    const path = LAUNCH_PATHS.find((candidate) => candidate.id === pathId);
    if (!user || !path) return null;
    const unlocked = await checkLaunchModuleUnlocked(path.moduleKey);
    await load();
    return { beta_path_id: pathId, module_unlocked: unlocked };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [user?.id, load]);

  return { paths, loading, refetch: load, evaluate };
}

/** Server-validated single-module unlock check, used by route gate. */
export async function checkLaunchModuleUnlocked(moduleKey: string): Promise<boolean> {
  const { data, error } = await (supabase).rpc("beta_module_unlocked", { _module_key: moduleKey });
  if (error) {
    console.warn("[checkLaunchModuleUnlocked] failed", error);
    return false;
  }
  return Boolean(data);
}